import {
  autoUpdate,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import MaxWidthContainer from 'components/MaxWidthContainer';
import ProductRankBadge from 'components/ProductRankBadge';
import WebpImg from 'components/WebpImg';
import useRWD from 'hooks/useRWD';
import _ from 'lodash';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Carousel } from 'react-responsive-carousel';

import { getProductWebpImageUrl } from 'utils';
import api from 'utils/api';

const { useState, useRef, useEffect } = React;

// 排名變化圖示元件
const RankChangeIcon = ({ isIncrease, size = 16 }) => {
  const color = isIncrease ? '#E9A302' : '#6E7F86';
  const path = isIncrease
    ? 'M11 1H19M19 1V9M19 1L11 9L7 5L1 11'
    : 'M11 11H19M19 11V3M19 11L11 3L7 7L1 1';
  // 灰色在視覺上看起來較細，稍微加粗
  const strokeWidth = isIncrease ? 2 : 2.3;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size * 0.6}
      viewBox="0 0 20 12"
      fill="none"
    >
      <path
        d={path}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const getRatingDisplay = (rating) => {
  if (!rating || rating.total === 0) return '-';
  const ratingPercentage = Math.round((rating.positive / rating.total) * 100);
  let emoji = '👌';
  if (ratingPercentage < 49) {
    emoji = '🤔';
  } else if (ratingPercentage >= 79) {
    emoji = '❤️';
  }
  return `${emoji} ${ratingPercentage}% `;
};

// 產品卡片元件
const RankingProductCard = ({
  product,
  rank,
  isFirst = false,
  categoryName = '',
  year,
  month,
  fadeOut = false,
}) => {
  const image = _.head(product.images);
  const rating = _.get(product, 'rating', null);
  // const rankChange = product.rankChange || rank == 1 ? 10 : -5;

  // 根據排名取得邊框顏色 class
  const getBorderColorClass = () => {
    switch (rank) {
      case 1:
        return 'border-[#F6AF0D]'; // 金色
      case 2:
        return 'border-[#6E7F86]'; // 銀色
      case 3:
        return 'border-[#D48B52]'; // 銅色
      default:
        return 'border-[#FCE1E5]'; // 粉色
    }
  };

  // 評率計算
  const ratingDisplay = getRatingDisplay(rating);

  if (isFirst) {
    // 第一名的大卡片
    return (
      <div
        className={`relative flex h-full cursor-pointer flex-col rounded-xl border-2 ${getBorderColorClass()} lh-[330px] w-[241px] bg-white p-4 ${fadeOut ? 'opacity-50' : ''}`}
        style={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)',
        }}
        onClick={() => {
          window.location = `/product/${product.id}`;
        }}
      >
        <div className="mx-auto mt-1 aspect-square h-[170px] w-[170px] overflow-hidden rounded-lg bg-white">
          <WebpImg
            src={getProductWebpImageUrl(image)}
            className="h-full w-full object-contain"
          />
          {/* 排名徽章 */}
        </div>
        <div className="absolute -top-1 left-0">
          <ProductRankBadge
            rank={rank}
            year={year}
            month={`${month}月`}
            categoryName={categoryName}
            width={78}
          />
        </div>
        <div className="font-notoSansTC mt-3 flex flex-col gap-y-1 text-left">
          <p className="text-[12px] text-gray-500">
            {_.get(product, 'brand.name', '')}
          </p>
          <p className="line-clamp-1 text-[14px] font-medium text-[#111928]">
            {product.en_name}
          </p>
          <p className="line-clamp-1 text-[14px] font-medium text-[#111928]">
            {product.name}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-x-2">
          {rating && rating.total > 0 && (
            <>
              <span className="text-[18px] font-bold text-sorra-pink">
                {ratingDisplay}
                <span className="text-[16px] font-bold text-[#9B9B9B]">
                  ({rating.total})
                </span>
              </span>
            </>
          )}
          {/* {rankChange !== 0 && (
            <span
              className={`flex items-center rounded px-1.5 py-0.5 text-[16px] font-medium ${rankChange > 0 ? 'bg-[#FFFBE7] text-[#E9A302]' : 'bg-[#F3F4F6] text-[#6E7F86]'}`}
            >
              <span className="mr-1">
                <RankChangeIcon isIncrease={rankChange > 0} size={16} />
              </span>
              {rankChange > 0 ? '+' : ''}
              {rankChange}
            </span>
          )} */}
        </div>
      </div>
    );
  }

  // 2-10 名的小卡片
  return (
    <div
      className={`relative flex cursor-pointer flex-row items-center gap-x-4 rounded-xl border ${getBorderColorClass()} h-[157px] w-[383px] bg-white p-3 ${fadeOut ? 'opacity-50' : ''}`}
      style={{
        boxShadow:
          '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)',
      }}
      onClick={() => {
        window.location = `/product/${product.id}`;
      }}
    >
      <div className="relative mx-auto h-[125px] w-[125px] shrink-0 overflow-hidden rounded-lg bg-white">
        <WebpImg
          src={getProductWebpImageUrl(image)}
          className="h-full w-full object-contain"
        />
        {/* 排名徽章 */}
      </div>
      <div className="absolute left-0 top-0">
        <ProductRankBadge
          rank={rank}
          year={year}
          month={`${month}月`}
          categoryName={categoryName}
          width={53}
        />
      </div>
      <div className="font-notoSansTC flex min-w-0 flex-1 flex-col gap-y-0.5 overflow-hidden text-left">
        <p className="line-clamp-1 text-[12px] text-gray-500">
          {_.get(product, 'brand.name', '')}
        </p>
        <p className="text-[#111928 line-clamp-1 text-[14px] font-medium">
          {product.en_name}
        </p>
        <p className="line-clamp-1 text-[14px] font-medium text-[#111928]">
          {product.name}
        </p>
        <div className="mt-1 flex items-center gap-x-2">
          {rating && rating.total > 0 && (
            <>
              <span className="text-[18px] font-bold text-sorra-pink">
                {ratingDisplay}
              </span>
              <span className="text-[18px] font-bold text-[#9B9B9B]">
                ({rating.total})
              </span>
            </>
          )}
          {/* {rank <= 3 && rankChange !== 0 && (
            <span
              className={`flex items-center rounded px-1 py-0.5 text-[16px] font-medium ${rankChange > 0 ? 'bg-[#FFFBE7] text-[#E9A302]' : 'bg-[#F3F4F6] text-[#6E7F86]'}`}
            >
              <span className="mr-1">
                <RankChangeIcon isIncrease={rankChange > 0} size={12} />
              </span>
              {rankChange > 0 ? '+' : ''}
              {rankChange}
            </span>
          )} */}
        </div>
      </div>
    </div>
  );
};

// 第一頁佈局組件 - 顯示 1~7 名
const FirstPageLayout = ({
  products,
  selectedCategory,
  currentYear,
  currentMonth,
  hasMore,
}) => {
  return (
    <div className="relative grid grid-cols-[241px_1fr_1fr_1fr] grid-rows-2 gap-4">
      {/* 第一名 - 大卡片 (左側, 跨2行) */}
      {products[0] && (
        <div className="row-span-2">
          <RankingProductCard
            product={products[0]}
            rank={1}
            isFirst={true}
            categoryName={selectedCategory}
            year={currentYear}
            month={currentMonth}
          />
        </div>
      )}

      {/* 第2名 (第1行第2欄) */}
      {products[1] && (
        <RankingProductCard
          product={products[1]}
          rank={2}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第4名 (第1行第3欄) */}
      {products[3] && (
        <RankingProductCard
          product={products[3]}
          rank={4}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第6名 (第1行第4欄) - 淡出效果 */}
      {products[5] ? (
        <RankingProductCard
          product={products[5]}
          rank={6}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
          fadeOut={hasMore}
        />
      ) : (
        <div />
      )}

      {/* 第3名 (第2行第2欄) */}
      {products[2] && (
        <RankingProductCard
          product={products[2]}
          rank={3}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第5名 (第2行第3欄) */}
      {products[4] && (
        <RankingProductCard
          product={products[4]}
          rank={5}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第7名 (第2行第4欄) - 淡出效果 */}
      {products[6] ? (
        <RankingProductCard
          product={products[6]}
          rank={7}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
          fadeOut={hasMore}
        />
      ) : (
        <div />
      )}
    </div>
  );
};

// 第二頁佈局組件 (顯示第6-10名，維持4欄佈局靠左對齊)
const SecondPageLayout = ({
  products,
  selectedCategory,
  currentYear,
  currentMonth,
}) => {
  // products 是第6-10名 (index 0-4 對應 rank 6-10)
  return (
    <div className="relative grid grid-cols-[1fr_1fr_1fr_1fr] grid-rows-2 gap-4">
      {/* 第6名 (第1行第1欄) */}
      {products[0] && (
        <RankingProductCard
          product={products[0]}
          rank={6}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第8名 (第1行第2欄) */}
      {products[2] && (
        <RankingProductCard
          product={products[2]}
          rank={8}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第10名 (第1行第3欄) */}
      {products[4] && (
        <RankingProductCard
          product={products[4]}
          rank={10}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 空白佔位 (第1行第4欄) */}
      <div />

      {/* 第7名 (第2行第1欄) */}
      {products[1] && (
        <RankingProductCard
          product={products[1]}
          rank={7}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 第9名 (第2行第2欄) */}
      {products[3] && (
        <RankingProductCard
          product={products[3]}
          rank={9}
          categoryName={selectedCategory}
          year={currentYear}
          month={currentMonth}
        />
      )}

      {/* 空白佔位 (第2行第3欄) */}
      <div />

      {/* 空白佔位 (第2行第4欄) */}
      <div />
    </div>
  );
};

// 手機版第一頁佈局組件 - 顯示 1~3 名
const MobileFirstPageLayout = ({
  products,
  selectedCategory,
  currentYear,
  currentMonth,
}) => {
  if (!products || products.length === 0) return null;
  return (
    <div className="flex min-[580px]:justify-center">
      <div className="flex gap-3">
        {/* 第一名 - 大卡片 (左側) */}
        {products[0] && (
          <RankingProductCard
            product={products[0]}
            rank={1}
            isFirst={true}
            categoryName={selectedCategory}
            year={currentYear}
            month={currentMonth}
          />
        )}

        {/* 右側：第2、3名垂直排列 */}
        <div className="flex flex-col gap-3">
          {/* 第2名 (右上) */}
          {products[1] && (
            <RankingProductCard
              product={products[1]}
              rank={2}
              categoryName={selectedCategory}
              year={currentYear}
              month={currentMonth}
            />
          )}

          {/* 第3名 (右下) */}
          {products[2] && (
            <RankingProductCard
              product={products[2]}
              rank={3}
              categoryName={selectedCategory}
              year={currentYear}
              month={currentMonth}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// 手機版雙產品頁面佈局組件 - 顯示 2~9 名
const MobileTwoProductsLayout = ({
  products,
  ranks,
  selectedCategory,
  currentYear,
  currentMonth,
}) => {
  return (
    <div className="flex min-[580px]:justify-center">
      <div className="grid grid-rows-2 gap-3">
        {products.map(
          (product, index) =>
            product && (
              <div key={product.id}>
                <RankingProductCard
                  product={product}
                  rank={ranks[index]}
                  categoryName={selectedCategory}
                  year={currentYear}
                  month={currentMonth}
                />
              </div>
            )
        )}
      </div>
    </div>
  );
};

// 手機版單產品頁面佈局組件 (用於第10名)
const MobileSingleProductLayout = ({
  product,
  rank,
  selectedCategory,
  currentYear,
  currentMonth,
}) => {
  if (!product) return null;
  return (
    <div className="flex min-[580px]:justify-center">
      <RankingProductCard
        product={product}
        rank={rank}
        categoryName={selectedCategory}
        year={currentYear}
        month={currentMonth}
      />
    </div>
  );
};

const HomeMonthlyRanking = () => {
  const { isLg } = useRWD();
  const carouselRef = useRef(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // 排行榜資訊狀態
  const [rankingInfo, setRankingInfo] = useState(null);
  const [hasNoData, setHasNoData] = useState(false); // 404 或無資料時為 true
  const [currentYear, setCurrentYear] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // 產品數據狀態
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 載入排行榜資訊（類別和日期）
  useEffect(() => {
    const fetchRankingInfo = async () => {
      try {
        const result = await api.get('/api/v4/monthlyRanking/info');
        const data = result.data;
        setRankingInfo(data);

        // 從 endsAt 取得年月（排行榜的月份）
        if (data.endsAt) {
          const endsAtDate = new Date(data.endsAt);
          setCurrentYear(endsAtDate.getFullYear());
          setCurrentMonth(endsAtDate.getMonth() + 1);
        }

        // 設置類別資料
        if (data.rankingTree && data.rankingTree.length > 0) {
          const formattedCategories = data.rankingTree.map((cat) => ({
            first_category_id: cat.id,
            first_category_name: cat.name,
            second_categories: cat.second_categories || [],
          }));
          setCategories(formattedCategories);

          // 設置預設選擇的類別
          const firstCategory = formattedCategories[0];
          if (
            firstCategory.second_categories &&
            firstCategory.second_categories.length > 0
          ) {
            setSelectedCategory(firstCategory.second_categories[0].name);
            setSelectedCategoryId(firstCategory.second_categories[0].id);
          } else {
            setSelectedCategory(firstCategory.first_category_name);
            setSelectedCategoryId(firstCategory.first_category_id);
          }
        } else {
          // 沒有類別資料
          setHasNoData(true);
        }
      } catch (error) {
        // 404 或其他錯誤，標記為無資料
        console.error('Error fetching ranking info:', error);
        setHasNoData(true);
        setIsLoading(false);
      }
    };

    fetchRankingInfo();
  }, []);

  // 載入產品數據
  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedCategoryId) return;

      setIsLoading(true);
      try {
        const result = await api.get(
          `/api/v4/monthlyRanking/category/${selectedCategoryId}`
        );
        // API 直接返回產品陣列
        setProducts(result.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [selectedCategoryId]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  // 手機版：顯示子類別時的主類別名稱 (null 表示顯示主類別列表)
  const [mobileSubcategoryView, setMobileSubcategoryView] = useState(null);

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isSelectorOpen,
    onOpenChange: (open) => {
      console.log(`onOpenChange ${open}`);
      setIsSelectorOpen(open);
      // 關閉時重置手機版子類別視圖
      if (!open) {
        console.log(`setMobileSubcategoryView ${open}`);
        setMobileSubcategoryView(null);
      }
    },
    placement: 'bottom-start',
    middleware: [offset(4), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  // 取前10個產品
  const displayProducts = products.slice(0, 10);

  // 桌面版：第一頁顯示1-5名，第二頁顯示6-10名
  const firstPageProducts = displayProducts.slice(0, 7);
  const secondPageProducts = displayProducts.slice(5, 10);

  // 計算總頁數
  const desktopTotalSlides = displayProducts.length > 5 ? 2 : 1;

  // 手機版頁數計算：第1頁(1~3名)、第2頁(2,3名)、第3頁(4,5名)、第4頁(6,7名)、第5頁(8,9名)、第6頁(10名)
  const getMobileTotalSlides = () => {
    const len = displayProducts.length;
    if (len === 0) return 0;
    if (len === 1) return 1;
    if (len <= 3) return 2;
    if (len <= 5) return 3;
    if (len <= 7) return 4;
    if (len <= 9) return 5;
    return 6;
  };
  const mobileTotalSlides = getMobileTotalSlides();

  const totalSlides = isLg ? desktopTotalSlides : mobileTotalSlides;
  const hasSecondPage = displayProducts.length > 5;

  const canGoLeft = currentSlide > 0;
  const canGoRight = currentSlide < totalSlides - 1;

  const handlePrev = () => {
    if (carouselRef.current && canGoLeft) {
      carouselRef.current.moveTo(currentSlide - 1);
    }
  };

  const handleNext = () => {
    if (carouselRef.current && canGoRight) {
      carouselRef.current.moveTo(currentSlide + 1);
    }
  };

  // 如果 API 返回 404 或無資料，不渲染任何內容
  if (hasNoData) {
    return null;
  }

  return (
    <MaxWidthContainer className="lg:py-22 relative py-8" noPending>
      {/* 背景圖 */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${isLg ? '/images/monthly-ranking-bg-desktop.png' : '/images/monthly-ranking-bg-mobile.png'})`,
        }}
      />

      {/* 內容 */}
      <div className="relative z-10 pl-4 pr-0 lg:pl-[74px] lg:pr-0">
        {/* 標題區 */}
        <div className="mb-6 flex flex-col gap-y-2 pr-4 lg:pr-0">
          <div className="block items-center gap-x-2 lg:flex">
            <h2 className="text-xl font-bold text-sorra-pink lg:text-2xl lg:text-3xl">
              Sorra 本月排行榜
            </h2>
            <button
              className="mt-1 flex items-center gap-x-1 text-sm font-medium text-sorra-pink lg:mt-0 lg:text-base"
              onClick={() => setIsInfoOpen(!isInfoOpen)}
            >
              <img
                src="/svgs/information-circle-pink.svg"
                alt=""
                className="mr-1 inline-block h-5 w-5"
              />
              Sorra 如何決定排行榜名次？
            </button>
          </div>
          <div
            className={`${isInfoOpen ? '' : 'hidden'} w-fit rounded-lg bg-white text-sm font-medium text-gray-700`}
          >
            <p className="px-3 py-2">
              排行榜根據 Sorra
              使用者在平台上的評論數量變化而定。每月統計所有產品過去 30
              天內的評論成長幅度，篩選出該類別中成長最快的 10
              款產品作為本月推薦榜單。
            </p>
          </div>
        </div>

        {/* 篩選區 */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div
            className="rounded-full border-2 border-sorra-pink px-4 py-2 text-base font-bold text-sorra-pink"
            style={{
              background:
                'radial-gradient(132.98% 110.07% at 93.4% 20.5%, rgba(251, 210, 216, 0.30) 3.61%, rgba(255, 237, 239, 0.30) 69.68%)',
            }}
          >
            {currentYear}年{currentMonth}月
          </div>
          <div className="relative">
            <button
              ref={refs.setReference}
              type="button"
              {...getReferenceProps()}
              className="flex h-[48px] min-w-[120px] items-center justify-between gap-x-2 rounded-full border-2 border-sorra-pink px-5 py-[14px] text-base font-bold text-sorra-pink"
              style={{
                background:
                  'radial-gradient(132.98% 110.07% at 93.4% 20.5%, rgba(251, 210, 216, 0.30) 3.61%, rgba(255, 237, 239, 0.30) 69.68%)',
              }}
            >
              <span className="whitespace-nowrap">{selectedCategory}</span>
              <ChevronDownIcon
                className={`h-6 w-6 shrink-0 text-sorra-pink transition-transform ${isSelectorOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {isSelectorOpen && (
              <FloatingPortal>
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  {...getFloatingProps()}
                  className="z-[100] flex items-start gap-x-2"
                >
                  {/* 桌面版：主類別 + 子類別並排 */}
                  {isLg ? (
                    <>
                      {/* 主類別列表 */}
                      <div className="min-w-[160px] shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
                        {categories.map((category) => (
                          <button
                            key={category.first_category_id}
                            type="button"
                            onMouseEnter={() =>
                              setHoveredCategory(category.first_category_name)
                            }
                            onClick={() => {
                              if (
                                !category.second_categories ||
                                category.second_categories.length === 0
                              ) {
                                setSelectedCategory(
                                  category.first_category_name
                                );
                                setSelectedCategoryId(
                                  category.first_category_id
                                );
                                setIsSelectorOpen(false);
                              }
                            }}
                            className="flex w-full items-center justify-between whitespace-nowrap border-b border-[#E7E7E7] px-4 py-3 text-left text-base font-semibold last:border-b-0 hover:bg-sorra-pink-light hover:text-sorra-pink"
                            style={{
                              background:
                                selectedCategory ===
                                  category.first_category_name ||
                                hoveredCategory === category.first_category_name
                                  ? 'radial-gradient(132.98% 110.07% at 93.4% 20.5%, rgba(251, 210, 216, 0.30) 3.61%, rgba(255, 237, 239, 0.30) 69.68%) text-sorra-pink '
                                  : '',
                            }}
                          >
                            <span>{category.first_category_name}</span>
                            {category.second_categories &&
                              category.second_categories.length > 0 && (
                                <ChevronRightIcon className="h-5 w-5 shrink-0 text-sorra-pink" />
                              )}
                          </button>
                        ))}
                      </div>
                      {/* 子類別列表 */}
                      {(() => {
                        const hoveredIndex = categories.findIndex(
                          (c) => c.first_category_name === hoveredCategory
                        );
                        const hoveredCategoryData = categories[hoveredIndex];
                        if (
                          !hoveredCategoryData ||
                          !hoveredCategoryData.second_categories?.length
                        )
                          return null;

                        // 每個項目高度約 48px (py-3 * 2 + 文字)
                        const itemHeight = 48;
                        const topOffset = hoveredIndex * itemHeight;

                        return (
                          <div
                            className="w-fit min-w-[160px] self-start overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
                            style={{ marginTop: topOffset }}
                          >
                            {hoveredCategoryData.second_categories.map(
                              (subCategory) => (
                                <button
                                  key={subCategory.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCategory(subCategory.name);
                                    setSelectedCategoryId(subCategory.id);
                                    setIsSelectorOpen(false);
                                  }}
                                  className="block w-full whitespace-nowrap border-b border-[#E7E7E7] px-4 py-3 text-left text-base font-medium text-gray-800 last:border-b-0 hover:bg-sorra-pink-light hover:text-sorra-pink"
                                >
                                  {subCategory.name}
                                </button>
                              )
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    /* 手機版：主類別或子類別切換顯示 */
                    <div
                      className={`${mobileSubcategoryView ? 'w-fit' : 'min-w-[160px]'} overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg`}
                    >
                      {mobileSubcategoryView ? (
                        /* 子類別視圖 */
                        <>
                          {/* 返回按鈕 */}
                          <button
                            type="button"
                            onClick={() => setMobileSubcategoryView(null)}
                            className="flex items-center gap-x-1 border-b border-[#E7E7E7] px-4 py-3 text-left text-base font-medium text-sorra-pink"
                          >
                            <ChevronRightIcon className="h-5 w-5 shrink-0 rotate-180 text-sorra-pink" />
                            <span>返回主類別</span>
                          </button>
                          {/* 子類別列表 */}
                          {categories
                            .find(
                              (c) =>
                                c.first_category_name === mobileSubcategoryView
                            )
                            ?.second_categories?.map((subCategory) => (
                              <button
                                key={subCategory.id}
                                type="button"
                                onClick={() => {
                                  setSelectedCategory(subCategory.name);
                                  setSelectedCategoryId(subCategory.id);
                                  setIsSelectorOpen(false);
                                  setMobileSubcategoryView(null);
                                }}
                                className="block w-full border-b border-[#E7E7E7] px-4 py-3 text-left text-base font-medium text-gray-800 last:border-b-0 hover:bg-sorra-pink-light hover:text-sorra-pink"
                              >
                                {subCategory.name}
                              </button>
                            ))}
                        </>
                      ) : (
                        /* 主類別視圖 */
                        categories.map((category) => (
                          <button
                            key={category.first_category_id}
                            type="button"
                            onClick={() => {
                              if (
                                category.second_categories &&
                                category.second_categories.length > 0
                              ) {
                                // 有子類別：切換到子類別視圖
                                setMobileSubcategoryView(
                                  category.first_category_name
                                );
                              } else {
                                // 無子類別：直接選擇
                                setSelectedCategory(
                                  category.first_category_name
                                );
                                setSelectedCategoryId(
                                  category.first_category_id
                                );
                                setIsSelectorOpen(false);
                              }
                            }}
                            className="flex w-full items-center justify-between whitespace-nowrap border-b border-[#E7E7E7] px-4 py-3 text-left text-base font-semibold last:border-b-0 hover:bg-sorra-pink-light hover:text-sorra-pink"
                            style={{
                              background:
                                selectedCategory ===
                                category.first_category_name
                                  ? 'radial-gradient(132.98% 110.07% at 93.4% 20.5%, rgba(251, 210, 216, 0.30) 3.61%, rgba(255, 237, 239, 0.30) 69.68%)'
                                  : '',
                            }}
                          >
                            <span>{category.first_category_name}</span>
                            {category.second_categories &&
                              category.second_categories.length > 0 && (
                                <ChevronRightIcon className="h-5 w-5 shrink-0 text-sorra-pink" />
                              )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </FloatingPortal>
            )}
          </div>
        </div>

        {/* 產品列表 */}
        {isLoading ? (
          <>
            {/* 桌面版 Skeleton */}
            <div className="hidden lg:grid lg:grid-cols-[241px_1fr_1fr_1fr] lg:grid-rows-2 lg:gap-4">
              {/* 第一名大卡片 Skeleton */}
              <div className="row-span-2 h-[330px] w-[241px] overflow-hidden rounded-xl">
                <Skeleton className="h-full w-full" />
              </div>
              {/* 2-7 名小卡片 Skeleton */}
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-[157px] overflow-hidden rounded-xl">
                  <Skeleton className="h-full w-full" />
                </div>
              ))}
            </div>
            {/* 手機版 Skeleton */}
            <div className="grid grid-cols-2 grid-rows-2 gap-3 lg:hidden">
              <div className="row-span-2 h-[330px] overflow-hidden rounded-xl">
                <Skeleton className="h-full w-full" />
              </div>
              <div className="h-[157px] overflow-hidden rounded-xl">
                <Skeleton className="h-full w-full" />
              </div>
              <div className="h-[157px] overflow-hidden rounded-xl">
                <Skeleton className="h-full w-full" />
              </div>
            </div>
          </>
        ) : displayProducts.length > 0 ? (
          <div className="relative">
            {/* 左箭頭 - 放在 overflow-hidden 外面 */}
            {isLg && canGoLeft && (
              <button
                onClick={handlePrev}
                className="absolute -left-10 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-md transition-colors hover:bg-gray-50"
              >
                <img
                  className="p-2.5"
                  src="/svgs/index-horizontalScroll-arrowLeft.svg"
                ></img>
              </button>
            )}

            {/* 右箭頭 - 放在 overflow-hidden 外面 */}
            {isLg && canGoRight && (
              <button
                onClick={handleNext}
                className="absolute right-5 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-md transition-colors hover:bg-gray-50"
              >
                <img
                  className="p-2.5"
                  src="/svgs/index-horizontalScroll-arrowRight.svg"
                ></img>
              </button>
            )}

            {/* Carousel 容器 - 只有這裡用 overflow-hidden */}
            <div
              className={`home-monthly-ranking-carousel overflow-hidden ${isLg ? 'block' : 'hidden'}`}
            >
              <Carousel
                ref={carouselRef}
                selectedItem={currentSlide}
                onChange={(index) => setCurrentSlide(index)}
                showArrows={false}
                showIndicators={false}
                showStatus={false}
                showThumbs={false}
                emulateTouch={true}
                swipeable={true}
                infiniteLoop={false}
                transitionTime={300}
              >
                {/* 第一頁 */}
                <div className="px-1">
                  <FirstPageLayout
                    products={firstPageProducts}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                    hasMore={hasSecondPage}
                  />
                </div>

                {/* 第二頁 */}
                {hasSecondPage && (
                  <div className="px-1">
                    <SecondPageLayout
                      products={secondPageProducts}
                      selectedCategory={selectedCategory}
                      currentYear={currentYear}
                      currentMonth={currentMonth}
                    />
                  </div>
                )}
              </Carousel>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-gray-500">暫無排行榜資料</div>
        )}

        {/* 手機版 Carousel */}
        {!isLoading && displayProducts.length > 0 && (
          <div
            className={`home-monthly-ranking-carousel overflow-hidden ${isLg ? 'hidden' : 'block'}`}
          >
            <Carousel
              ref={carouselRef}
              selectedItem={currentSlide}
              onChange={(index) => setCurrentSlide(index)}
              showArrows={false}
              showIndicators={false}
              showStatus={false}
              showThumbs={false}
              emulateTouch={true}
              swipeable={true}
              infiniteLoop={false}
              transitionTime={300}
            >
              {/* 手機版第1頁：第1~3名 */}
              <div className="px-1">
                <MobileFirstPageLayout
                  products={displayProducts.slice(0, 3)}
                  selectedCategory={selectedCategory}
                  currentYear={currentYear}
                  currentMonth={currentMonth}
                />
              </div>

              {/* 手機版第2頁：第2、3名 */}
              {displayProducts.length > 1 && (
                <div className="px-1">
                  <MobileTwoProductsLayout
                    products={[displayProducts[1], displayProducts[2]]}
                    ranks={[2, 3]}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                  />
                </div>
              )}

              {/* 手機版第3頁：第4、5名 */}
              {displayProducts.length > 3 && (
                <div className="px-1">
                  <MobileTwoProductsLayout
                    products={[displayProducts[3], displayProducts[4]]}
                    ranks={[4, 5]}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                  />
                </div>
              )}

              {/* 手機版第4頁：第6、7名 */}
              {displayProducts.length > 5 && (
                <div className="px-1">
                  <MobileTwoProductsLayout
                    products={[displayProducts[5], displayProducts[6]]}
                    ranks={[6, 7]}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                  />
                </div>
              )}

              {/* 手機版第5頁：第8、9名 */}
              {displayProducts.length > 7 && (
                <div className="px-1">
                  <MobileTwoProductsLayout
                    products={[displayProducts[7], displayProducts[8]]}
                    ranks={[8, 9]}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                  />
                </div>
              )}

              {/* 手機版第6頁：第10名 */}
              {displayProducts.length > 9 && (
                <div className="px-1">
                  <MobileSingleProductLayout
                    product={displayProducts[9]}
                    rank={10}
                    selectedCategory={selectedCategory}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                  />
                </div>
              )}
            </Carousel>
          </div>
        )}

        {/* 查看全部按鈕 */}
        {displayProducts.length > 0 && (
          <div className="mr-4 mt-6 flex justify-center lg:mr-0">
            <button
              className="w-full rounded-lg border border-sorra-pink bg-white px-12 py-3 text-base font-medium text-sorra-pink transition-colors hover:bg-sorra-pink hover:text-white lg:w-[343px]"
              onClick={() => {
                window.location = `/monthlyRanking?category=${selectedCategoryId}`;
              }}
            >
              查看全部
            </button>
          </div>
        )}
      </div>
    </MaxWidthContainer>
  );
};

export default HomeMonthlyRanking;
